/*
 * Legacy lander combined buy-box behavior. Loaded via `snippets/yo-deferred-asset.liquid`,
 * which deflects the inline-script TBT cost into a first-interaction-or-onload-idle window.
 *
 * The companion section renders action `<button>` elements with `disabled data-legacy-pending="true"`
 * so a native form submit cannot post to `/cart/add` and redirect to `/cart` before
 * `initCombinedBuybox` finishes binding listeners. `releasePendingButtons` removes both
 * attributes at the end of init, so the first clickable moment has every listener in place.
 *
 * Top-level `__yoLoaded.legacyBuybox` guard is the asset-level layer of the loader snippet's
 * triple guard -- see its header for the full rationale.
 */
(function () {
  window.__yoLoaded = window.__yoLoaded || {};
  if (window.__yoLoaded.legacyBuybox) return;
  window.__yoLoaded.legacyBuybox = true;

  const isMobile = () => window.matchMedia("(max-width: 899px)").matches;

  function cssEscape(id) {
    if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(id);
    return String(id).replace(/[^a-zA-Z0-9_\-]/g, "\\$&");
  }

  function setLoadingState(form, isLoading, submitter) {
    const btn =
      submitter ||
      form.querySelector('[type="submit"]') ||
      form.querySelector("button") ||
      form.querySelector('input[type="submit"]');

    if (!btn) return;

    if (!btn.dataset.originalHtml) {
      btn.dataset.originalHtml = btn.tagName === "BUTTON" ? btn.innerHTML : btn.value;
    }

    if (isLoading) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      btn.classList.add("is-loading");

      if (btn.tagName === "BUTTON") {
        btn.innerHTML = `
          <span class="btn-spinner" aria-hidden="true"></span>
        `;
      }
    } else {
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
      btn.classList.remove("is-loading");

      if (btn.tagName === "BUTTON") btn.innerHTML = btn.dataset.originalHtml;
      else btn.value = btn.dataset.originalHtml;
    }
  }

  function resetBuyNowLoadingStates(root) {
    const scope = root || document;
    scope.querySelectorAll(".buy-now-form").forEach((form) => {
      form.querySelectorAll(".is-loading").forEach((btn) => {
        btn.disabled = false;
        btn.classList.remove("is-loading");
        btn.removeAttribute("aria-busy");

        if (btn.dataset.originalHtml && btn.tagName === "BUTTON") btn.innerHTML = btn.dataset.originalHtml;
        if (btn.dataset.originalHtml && btn.tagName !== "BUTTON") btn.value = btn.dataset.originalHtml;
      });
    });
  }

  /*
   * Release the `disabled`/`data-legacy-pending` gate the section markup paints
   * onto interactive buttons. Run this once all listeners for the section have
   * been bound, so a click on a now-enabled button always lands on a real handler.
   */
  function releasePendingButtons(root) {
    root.querySelectorAll("button[data-legacy-pending]").forEach((btn) => {
      btn.removeAttribute("disabled");
      btn.removeAttribute("data-legacy-pending");
      btn.removeAttribute("aria-busy");
    });
  }

  if (document.readyState !== "loading") {
    resetBuyNowLoadingStates();
  } else {
    document.addEventListener("DOMContentLoaded", () => resetBuyNowLoadingStates(), { once: true });
  }
  window.addEventListener("pageshow", () => resetBuyNowLoadingStates());

  function formDataToItem(formData) {
    const id = Number(formData.get("id"));
    const quantity = Number(formData.get("quantity") || 1);
    const sellingPlan = formData.get("selling_plan");

    const properties = {};
    for (const [k, v] of formData.entries()) {
      const m = String(k).match(/^properties\[(.+)\]$/);
      if (m) properties[m[1]] = v;
    }

    const item = {
      id,
      quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
    };

    if (sellingPlan) item.selling_plan = sellingPlan;
    if (Object.keys(properties).length) item.properties = properties;

    return item;
  }

  async function addPrimaryFromForm(form) {
    const fd = new FormData(form);
    const primary = formDataToItem(fd);
    let items = [primary];
    const productId = (form.getAttribute("data-product-id") || form.dataset.productId || "").trim();
    if (productId && window.GWPCart && typeof window.GWPCart.pairGiftLineItemsForProduct === "function") {
      let coAdds = [];
      try {
        coAdds = await Promise.resolve(window.GWPCart.pairGiftLineItemsForProduct(productId));
      } catch (e) { 
        coAdds = [];
      }
      const primaryId = Number(primary.id);
      for (let i = 0; i < coAdds.length; i++) {
        const g = coAdds[i];
        if (g && Number(g.id) !== primaryId) {
          items.push(g);
        }
      }
    }

    const r = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });

    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw err;
    }
  }

  function initCombinedBuybox(root) {
    if (!root || root.dataset.combinedBuyboxBound === "1") return;
    root.dataset.combinedBuyboxBound = "1";

    const sid = root.getAttribute("data-section-id") || "";

    const subscriptionModal = root.querySelector("#subscriptionModal");
    const subscribeAndSaveModal = root.querySelector("#modal_subscribe_and_save");

    function openTab(btnEl, tabId, options) {
      const preserveAccordionState = options && options.preserveAccordionState === true;

      root.querySelectorAll(".tabcontent").forEach((el) => {
        el.style.display = "none";
        el.hidden = true;
      });
      root.querySelectorAll(".tablinks").forEach((btn) => btn.classList.remove("active"));

      const target = root.querySelector(`#${cssEscape(tabId)}`);
      if (target) {
        target.hidden = false;
        target.style.display = isMobile() ? "inline-block" : "block";
      }

      if (btnEl) btnEl.classList.add("active");

      if (!preserveAccordionState) {
        if (tabId === `Subscribe-${sid}`) {
          initializeMobileBoxes("subscribe");
        } else if (tabId === `OTP-${sid}`) {
          initializeMobileBoxes("otp");
        }
      }
    }

    function setupAccordionPanel(containerEl, centerTargetBase) {
      if (!containerEl) return;

      const centerTarget = centerTargetBase ? `${centerTargetBase}-${sid}` : "";

      if (!containerEl.dataset.accordionBound) {
        containerEl.addEventListener("click", (e) => {
          const box = e.target.closest(".mobile_boxes");
          if (!box || !containerEl.contains(box)) return;

          const targetIdRaw = box.getAttribute("data-target");
          if (!targetIdRaw) return;

          const content =
            containerEl.querySelector(`#${cssEscape(targetIdRaw)}`) ||
            root.querySelector(`#${cssEscape(targetIdRaw)}`);

          if (!content) return;

          const willOpen = !content.classList.contains("open");

          containerEl.querySelectorAll(".mobile_boxes.open").forEach((b) => b.classList.remove("open"));
          containerEl.querySelectorAll(".content.open").forEach((c) => c.classList.remove("open"));

          if (willOpen) {
            box.classList.add("open");
            content.classList.add("open");
          } else {
            const centerBox =
              (centerTarget && containerEl.querySelector(`.mobile_boxes[data-target="${centerTarget}"]`)) ||
              (centerTargetBase && containerEl.querySelector(`.mobile_boxes[data-target="${centerTargetBase}"]`));

            const centerContent =
              (centerTarget &&
                (containerEl.querySelector(`#${cssEscape(centerTarget)}`) || root.querySelector(`#${cssEscape(centerTarget)}`))) ||
              (centerTargetBase &&
                (containerEl.querySelector(`#${cssEscape(centerTargetBase)}`) || root.querySelector(`#${cssEscape(centerTargetBase)}`)));

            if (centerBox && centerContent) {
              centerBox.classList.add("open");
              centerContent.classList.add("open");
            }
          }
        });
        containerEl.dataset.accordionBound = "1";
      }

      if (isMobile()) {
        const centerBox =
          (centerTarget && containerEl.querySelector(`.mobile_boxes[data-target="${centerTarget}"]`)) ||
          (centerTargetBase && containerEl.querySelector(`.mobile_boxes[data-target="${centerTargetBase}"]`));

        const centerContent =
          (centerTarget &&
            (containerEl.querySelector(`#${cssEscape(centerTarget)}`) || root.querySelector(`#${cssEscape(centerTarget)}`))) ||
          (centerTargetBase &&
            (containerEl.querySelector(`#${cssEscape(centerTargetBase)}`) || root.querySelector(`#${cssEscape(centerTargetBase)}`)));

        if (centerBox && centerContent) {
          containerEl.querySelectorAll(".mobile_boxes.open").forEach((b) => b.classList.remove("open"));
          containerEl.querySelectorAll(".content.open").forEach((c) => c.classList.remove("open"));
          centerBox.classList.add("open");
          centerContent.classList.add("open");
        }
      }
    }

    function initializeMobileBoxes(tabType) {
      const subscribePanel = root.querySelector(`#Subscribe-${cssEscape(sid)}`);
      const otpPanel = root.querySelector(`#OTP-${cssEscape(sid)}`);

      if (tabType === "subscribe") {
        setupAccordionPanel(subscribePanel, "mobile_center_box");
      } else if (tabType === "otp") {
        setupAccordionPanel(otpPanel, "otp_mobile_center_box");
      }
    }

    root.querySelectorAll(".tablinks[data-tab]").forEach((btn) => {
      if (btn.dataset.tabBound === "1") return;
      btn.dataset.tabBound = "1";

      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const tabId = btn.getAttribute("data-tab");
        if (tabId) openTab(btn, tabId);
      });
    });

    function closeById(id) {
      if (!id) return;
      const el = root.querySelector(`#${cssEscape(id)}`) || document.getElementById(id);
      if (el) el.style.display = "none";
    }

    function openById(id) {
      if (!id) return;
      const el = root.querySelector(`#${cssEscape(id)}`) || document.getElementById(id);
      if (el) el.style.display = "flex";
    }

    root.querySelectorAll("[data-close]").forEach((el) => {
      if (el.dataset.closeBound === "1") return;
      el.dataset.closeBound = "1";

      el.addEventListener("click", (e) => {
        e.preventDefault();
        closeById(el.getAttribute("data-close"));
      });
    });

    root.querySelectorAll("[data-open]").forEach((el) => {
      if (el.dataset.openBound === "1") return;
      el.dataset.openBound = "1";

      el.addEventListener("click", (e) => {
        e.preventDefault();
        openById(el.getAttribute("data-open"));
      });
    });

    root.addEventListener("click", (e) => {
      if (subscribeAndSaveModal && e.target === subscribeAndSaveModal) subscribeAndSaveModal.style.display = "none";
    });

    if (subscriptionModal) {
      subscriptionModal.addEventListener("click", (e) => {
        if (e.target === subscriptionModal) {
          subscriptionModal.style.display = "none";
          return;
        }

        const clickedClose = e.target.closest(".modal-cta, .view_more_modal-close");
        if (clickedClose) {
          e.preventDefault();
          subscriptionModal.style.display = "none";
        }
      });
    }

    root.querySelectorAll(".buy-now-form").forEach((form) => {
      if (form.dataset.buyNowBound === "1") return;
      form.dataset.buyNowBound = "1";

      form.addEventListener("submit", async (e) => {
        e.preventDefault();

        const submitter = e.submitter;
        setLoadingState(form, true, submitter);

        try {
          await addPrimaryFromForm(form);
          if (window.GWPCart && typeof window.GWPCart.sync === "function") {
            await window.GWPCart.sync();
          }
          window.location.href = "/checkout";
        } catch (err) {
          console.error("Add to cart failed:", err);
          setLoadingState(form, false, submitter);
        }
      });
    });

    root.querySelectorAll(".buybox_left, .buybox_center, .buybox_right").forEach((box) => {
      if (box.dataset.buyboxClickBound === "1") return;
      box.dataset.buyboxClickBound = "1";

      box.addEventListener("click", (e) => {
        if (!e.target.closest("button") && !e.target.closest("a")) {
          const form = box.querySelector("form.buy-now-form");
          if (form) form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
        }
      });
    });

    const defaultBtn = root.querySelector(`#defaultOpen-${cssEscape(sid)}`) || root.querySelector(".tablinks[data-tab]");
    const defaultTabId = `Subscribe-${sid}`;

    if (defaultBtn) {
      openTab(defaultBtn, defaultTabId);
    } else {
      const panel = root.querySelector(`#${cssEscape(defaultTabId)}`);
      if (panel) {
        panel.hidden = false;
        panel.style.display = isMobile() ? "inline-block" : "block";
      }
      initializeMobileBoxes("subscribe");
    }

    // Preserve accordion state on resize (mobile address-bar show/hide used to reset
    // selection to "3 bottles"). The unload handler removes the listener so Theme
    // Editor re-renders do not leak stale closures over the detached `root`.
    let resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const activeBtn = root.querySelector(".tablinks.active");
        const otpPanel = root.querySelector(`#OTP-${cssEscape(sid)}`);
        const otpShowing = otpPanel ? otpPanel.style.display !== "none" : false;

        const showingId = otpShowing ? `OTP-${sid}` : `Subscribe-${sid}`;
        openTab(activeBtn, showingId, { preserveAccordionState: true });
      }, 120);
    }
    window.addEventListener("resize", onResize);

    document.addEventListener("shopify:section:unload", function onUnload(e) {
      if (!root.contains(e.target) && e.target !== root.closest(".shopify-section")) return;
      window.removeEventListener("resize", onResize);
      document.removeEventListener("shopify:section:unload", onUnload);
    });

    releasePendingButtons(root);
  }

  function boot() {
    document.querySelectorAll("[data-combined-buybox]").forEach(initCombinedBuybox);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
})();
